import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Printer, Mail, ArrowDown, User, Briefcase, ChevronRight, X, Building, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ContactCard = ({ item, index }) => {
    const { t } = useTranslation();
    const Icon = {
        MapPin,
        Phone,
        Printer,
        Mail
    }[item.icon];
    return (
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        viewport={{ once: true }}
        className="w-full"
      >
        <div className="h-full bg-white/40 backdrop-blur-xl border border-white/20 rounded-3xl p-8 transform hover:-translate-y-3 transition-transform duration-400 ease-in-out shadow-lg hover:shadow-2xl flex flex-col items-center text-center text-gray-800 group">
          <div className={`relative w-24 h-24 flex items-center justify-center rounded-full mb-6 bg-gradient-to-br ${item.gradient} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-10 h-10 text-white relative z-10" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">{t(item.title)}</h3>
          <div className="text-gray-700 space-y-1 font-medium">
            {item.lines.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
        </div>
      </motion.div>
    );
};

const StaticArtisticBackground = () => {
    return (
        <div className="absolute inset-0 -z-10 overflow-hidden bg-[#f0f4ff]">
            <div className="absolute top-0 -left-1/4 w-full h-full bg-[radial-gradient(ellipse_at_center,_rgba(168,85,247,0.1)_0%,_rgba(168,85,247,0)_40%)]"></div>
            <div className="absolute -bottom-1/4 right-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.1)_0%,_rgba(59,130,246,0)_50%)]"></div>
            <div className="absolute bottom-[-20%] left-[5%] w-full h-full bg-[radial-gradient(circle_at_center,_rgba(236,72,153,0.1)_0%,_rgba(236,72,153,0)_40%)]"></div>
        </div>
    );
}

const Contact = () => {
  const { t } = useTranslation();
  const [selectedPerson, setSelectedPerson] = useState(null);

  const deanInfo = t('contact_page.deanInfo', { returnObjects: true });
  const viceDeansInfo = t('contact_page.viceDeansData', { returnObjects: true });
  const facultySecretaryInfo = t('contact_page.facultySecretaryInfo', { returnObjects: true });
  const deanshipStaff = t('contact_page.deanshipStaff', { returnObjects: true });
  const contactInfo = t('contact_page.contactInfo', { returnObjects: true });

  const scrollToContent = () => {
    document.getElementById('contact-content').scrollIntoView({
      behavior: 'smooth'
    });
  };

  return (
    <div className="font-sans bg-[#f0f4ff]">
      {/* Full-screen Video Hero */}
      <div className="h-screen w-full relative overflow-hidden">
        <video
          className="absolute top-1/2 left-1/2 w-full h-full object-cover transform -translate-x-1/2 -translate-y-1/2"
          src="/fenfakultesivideo.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center">
            <motion.h1
              className="text-5xl md:text-8xl font-black text-white uppercase"
              style={{ textShadow: '0px 5px 20px rgba(0,0,0,0.5)' }}
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              {t('contact_page.hero.title')}
            </motion.h1>
            <motion.p
              className="mt-4 text-lg md:text-xl text-white/90 font-medium max-w-2xl"
              style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              {t('contact_page.hero.subtitle')}
            </motion.p>
              </div>
        <motion.div
            className="absolute bottom-10 left-1/2 -translate-x-1/2 cursor-pointer z-20"
            onClick={scrollToContent}
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
            <ArrowDown className="w-10 h-10 text-white/80 hover:text-white transition-colors"/>
        </motion.div>
              </div>
              
      {/* Page Content */}
      <div id="contact-content" className="relative z-10 py-24 overflow-hidden">
        <StaticArtisticBackground />
        <div className="relative z-10 container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
            {contactInfo && contactInfo.map((item, index) => (
              <ContactCard item={item} index={index} key={index} />
            ))}
              </div>
              
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
            {/* Map Section */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-2 bg-white/40 backdrop-blur-xl border-white/20 rounded-3xl overflow-hidden shadow-lg h-full"
            >
                <iframe
                  title={t('contact_page.map.title')}
                  src="https://maps.google.com/maps?q=Hacettepe%20%C3%9Cniversitesi%20Fen%20Fak%C3%BCltesi&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-96 lg:h-full lg:min-h-[650px] border-0"
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
            </motion.div>
            
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="lg:col-span-3 bg-white/40 backdrop-blur-xl border-white/20 rounded-3xl p-8 lg:p-12 text-gray-800 shadow-lg"
            >
              <h2 className="text-4xl font-bold text-center text-gray-900 mb-2">{t('contact_page.form.title')}</h2>
              <p className="text-center text-gray-600 mb-10 font-medium">{t('contact_page.form.subtitle')}</p>
              <form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_page.form.name')}</label>
                    <input type="text" id="name" className="w-full px-4 py-3 bg-white/50 border-2 border-white/40 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-300" />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_page.form.email')}</label>
                    <input type="email" id="email" className="w-full px-4 py-3 bg-white/50 border-2 border-white/40 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-300" />
                  </div>
                </div>
                  <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_page.form.subject')}</label>
                  <input type="text" id="subject" className="w-full px-4 py-3 bg-white/50 border-2 border-white/40 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-300" />
                </div>
                  <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">{t('contact_page.form.message')}</label>
                  <textarea id="message" rows="5" className="w-full px-4 py-3 bg-white/50 border-2 border-white/40 rounded-xl focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-all duration-300"></textarea>
                  </div>
                <div className="text-center pt-4">
                  <button type="submit" className="bg-gradient-to-r from-violet-600 to-purple-600 text-white font-bold py-4 px-10 rounded-xl hover:from-violet-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                    {t('contact_page.form.send')}
                  </button>
                </div>
              </form>
            </motion.div>
              </div>
            </div>

        {/* Management Section */}
        <div className="py-24">
            <div className="container mx-auto px-4">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-16"
                >
                    <h2 className="text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">{t('contact_page.management.title')}</h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('contact_page.management.subtitle')}</p>
                </motion.div>

                {/* Dean Section */}
                <div className="flex justify-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, ease: 'easeOut' }}
                        viewport={{ once: true }}
                        className="group cursor-pointer w-full max-w-sm"
                        onClick={() => setSelectedPerson(deanInfo)}
                    >
                        <div className="relative bg-gradient-to-br from-white to-gray-100 backdrop-blur-2xl border-2 border-white rounded-3xl p-8 text-center shadow-2xl hover:shadow-purple-200/50 transition-all duration-500 ease-in-out transform hover:-translate-y-2 h-full flex flex-col">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold py-2 px-6 rounded-full shadow-lg">
                                {t('contact_page.deanInfo.role')}
                            </div>
                            <div className="relative mb-6">
                                <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-4 border-white shadow-xl">
                                    <img 
                                        src={deanInfo.photo}
                                        alt={deanInfo.name}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                            <div className="flex-grow">
                                <h3 className="text-2xl font-bold text-gray-900 mb-1">{deanInfo.name}</h3>
                                <p className="text-purple-700 font-semibold">{deanInfo.role}</p>
                            </div>
                            <div className="mt-8">
                                <div className="flex items-center justify-center gap-2 text-sm font-semibold text-indigo-700 group-hover:text-purple-700 transition-colors">
                                    {t('contact_page.view_details')} <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Vice Deans Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20 max-w-4xl mx-auto">
                    {viceDeansInfo && viceDeansInfo.map((staff, index) => (
                        <motion.div
                            key={staff.id}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="group cursor-pointer"
                            onClick={() => setSelectedPerson(staff)}
                        >
                            <div className="relative bg-white/60 backdrop-blur-xl border border-white/30 rounded-3xl p-8 text-center shadow-xl hover:shadow-indigo-200/50 transition-all duration-400 ease-in-out transform hover:-translate-y-2 h-full flex flex-col">
                                 <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-indigo-500 to-sky-500 text-white font-semibold py-1.5 px-5 rounded-full shadow-md text-sm">
                                    {t('contact_page.viceDeansInfo.role')}
                                </div>
                                <div className="relative mb-5">
                                    <div className="w-32 h-32 mx-auto rounded-full flex items-center justify-center bg-gray-200 overflow-hidden border-4 border-white/50 shadow-md">
                                        {staff.photo ? (
                                            <img src={staff.photo} alt={staff.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <User className="w-16 h-16 text-gray-400" />
                                        )}
                                    </div>
                                </div>
                                <div className="flex-grow">
                                    <h3 className="text-xl font-bold text-gray-900 mb-1">{staff.name}</h3>
                                    <p className="text-indigo-600 font-medium">{staff.role}</p>
                                </div>
                                <div className="mt-6">
                                    <div className="flex items-center justify-center gap-2 text-sm font-semibold text-sky-700 group-hover:text-indigo-700 transition-colors">
                                        {t('contact_page.view_details')} <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Faculty Secretary Section */}
                <div className="flex justify-center mb-20">
                     <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                        viewport={{ once: true }}
                        className="group cursor-pointer w-full max-w-md"
                        onClick={() => setSelectedPerson(facultySecretaryInfo)}
                    >
                        <div className="relative bg-white/80 backdrop-blur-xl border border-white/40 rounded-3xl p-8 text-center shadow-xl hover:shadow-cyan-200/50 transition-all duration-400 ease-in-out transform hover:-translate-y-2 h-full flex flex-col">
                             <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-semibold py-1.5 px-5 rounded-full shadow-md text-sm">
                                {t('contact_page.facultySecretaryInfo.role')}
                            </div>
                            <div className="relative mb-5">
                                <div className="w-32 h-32 mx-auto rounded-full flex items-center justify-center bg-gray-200 overflow-hidden border-4 border-white/50 shadow-md">
                                    {facultySecretaryInfo.photo ? (
                                        <img src={facultySecretaryInfo.photo} alt={facultySecretaryInfo.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <User className="w-16 h-16 text-gray-400" />
                                    )}
                                </div>
                            </div>
                            <div className="flex-grow">
                                <h3 className="text-xl font-bold text-gray-900 mb-1">{facultySecretaryInfo.name}</h3>
                                <p className="text-cyan-600 font-medium">{facultySecretaryInfo.role}</p>
                            </div>
                            <div className="mt-6">
                                <div className="flex items-center justify-center gap-2 text-sm font-semibold text-teal-700 group-hover:text-cyan-700 transition-colors">
                                    {t('contact_page.view_details')} <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>


                {/* Dean's Office Staff Section */}
                <div className="border-t border-gray-200 pt-16">
                     <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h3 className="text-3xl font-bold text-gray-800 mb-3">{t('contact_page.deanship_staff.title')}</h3>
                        <p className="text-lg text-gray-500 max-w-2xl mx-auto">{t('contact_page.deanship_staff.subtitle')}</p>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                        {deanshipStaff && deanshipStaff.map((staff, index) => (
                            <motion.div
                                key={staff.id}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.05 }}
                                viewport={{ once: true }}
                                className="group cursor-pointer"
                                onClick={() => setSelectedPerson(staff)}
                            >
                                <div className="relative bg-white/40 backdrop-blur-xl border-white/20 rounded-3xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-400 ease-in-out transform hover:-translate-y-2 h-full flex flex-col">
                                    <div className="relative mb-4">
                                        <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center bg-gray-200 overflow-hidden border-4 border-white/50 shadow-md">
                                          <User className="w-12 h-12 text-gray-400" />
                                        </div>
                                    </div>
                                    <div className="flex-grow">
                                        <h3 className="text-lg font-bold text-gray-900 mb-1">{staff.name}</h3>
                                        <p className="text-gray-600 font-medium text-sm">{staff.role}</p>
                                    </div>
                                    <div className="mt-5">
                                        <div className="flex items-center justify-center gap-2 text-xs font-semibold text-violet-700 group-hover:text-purple-700 transition-colors">
                                            {t('contact_page.view_details')} <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
            </div>
          </div>
        </div>
      </div>

      {/* Staff Detail Modal */}
      {selectedPerson && (
        <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.4, ease: [0.25, 1, 0.5, 1] }}
                className="relative max-w-lg w-full bg-gradient-to-br from-gray-50 to-gray-200 rounded-3xl shadow-2xl overflow-hidden"
            >
                <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-purple-600 to-indigo-600 opacity-80"></div>
                
                <button 
                    onClick={() => setSelectedPerson(null)}
                    className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors z-20"
                >
                    <X className="w-7 h-7"/>
                </button>

                <div className="relative p-8">
                    <div className="relative z-10 flex flex-col items-center -mt-16">
                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-xl mx-auto flex items-center justify-center bg-gray-200 flex-shrink-0">
                           {selectedPerson.photo ? (
                                <img 
                                    src={selectedPerson.photo} 
                                    alt={selectedPerson.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <User className="w-20 h-20 text-gray-400" />
                            )}
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mt-4 text-center">{selectedPerson.name}</h2>
                        <p className="text-indigo-600 font-semibold text-lg text-center">{selectedPerson.role}</p>
                    </div>

                    <div className="mt-8 pt-8 border-t border-gray-300/70">
                        <div className="space-y-5">
                            {selectedPerson.unit && (
                              <div className="flex items-start gap-4">
                                  <div className="bg-violet-100 text-violet-600 p-3 rounded-full"><Building className="w-6 h-6"/></div>
                                  <div>
                                      <p className="text-sm text-gray-500 font-medium">{t('contact_page.modal.unit')}</p>
                                      <p className="font-semibold text-gray-800 text-lg">{selectedPerson.unit}</p>
                                  </div>
                              </div>
                            )}
                            {selectedPerson.phone && (
                                <div className="flex items-start gap-4">
                                    <div className="bg-rose-100 text-rose-600 p-3 rounded-full"><Phone className="w-6 h-6"/></div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">{t('contact_page.modal.phone')}</p>
                                        <p className="font-semibold text-gray-800 text-lg">{selectedPerson.phone}</p>
                                    </div>
                                </div>
                            )}
                            {selectedPerson.email && (
                                <div className="flex items-start gap-4">
                                    <div className="bg-pink-100 text-pink-600 p-3 rounded-full"><Mail className="w-6 h-6"/></div>
                                    <div>
                                        <p className="text-sm text-gray-500 font-medium">{t('contact_page.modal.email')}</p>
                                        <p className="font-semibold text-gray-800 text-lg">{selectedPerson.email}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Contact;